import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { CustomersService } from 'src/app/services/customers.service';
import { PluginsService } from 'src/app/services/plugins.service';
import { UpdatePluginCustomerConfigurationFile } from 'src/app/states/customers-state/customers.actions';
import { CustomersState } from 'src/app/states/customers-state/customers.state';
import { UpdatePluginConfigurationFile } from 'src/app/states/plugins-state/plugins.actions';
import { PluginsState } from 'src/app/states/plugins-state/plugins.state';
import { RightsService } from '../../../services/rights.service';

@Component({
  selector: 'app-plugin-file',
  templateUrl: './plugin-file.component.html',
  styleUrls: ['./plugin-file.component.scss']
})
export class PluginFileComponent implements OnInit, OnDestroy {

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('fileInput') public fileInput!: any;
  
  @Select(PluginsState.getSelectedPluginId)
  public selectedPluginId$!: Observable<string>;
  @Select(CustomersState.getSelectedCustomerId)
  public selectedCustomerId$!: Observable<string>;
  @Select(CustomersState.getActivePluginId)
  public activePluginId$!: Observable<string>;

  @Input() public isInCustomerConfigurationMode!: boolean;
  @Input() public file!: AbstractControl;
  public fileChanged = false;
  public filePresentAtInit = false;
  private pluginId!: string;
  private customerId!: string;
  private subs = new Array<Subscription>();

  public constructor(
    private accessService: RightsService,
    private store: Store,
    private pluginsService: PluginsService,
    private customersService: CustomersService
    )
    { }

  public ngOnInit(): void {
    if (this.isInCustomerConfigurationMode) {
      this.subs.push(this.selectedCustomerId$.subscribe(customerId => {
        if (customerId !== '') {
          this.customerId = customerId;
        }
      }));
      this.subs.push(this.activePluginId$.subscribe(activePluginId => {
        if (activePluginId !== '') {
          this.pluginId = activePluginId;
        }
      }));
    }
    else {
      this.subs.push(this.selectedPluginId$.subscribe(pluginId => {
        if (pluginId !== '') {
          this.pluginId = pluginId;
        }
      }));
    }

    if (this.file.get('fileName')?.value) {
      this.filePresentAtInit = true;
    }
  }

  public ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  public saveFile(): void {
    if (this.isInCustomerConfigurationMode) {
      this.subs.push(this.store.dispatch(new UpdatePluginCustomerConfigurationFile(this.pluginId, this.customerId, this.file.value))
        .subscribe(() => {
          this.filePresentAtInit = this.file.get('fileName')?.value !== null;
          this.fileChanged = false;
        }));
    }
    else {
      this.subs.push(this.store.dispatch(new UpdatePluginConfigurationFile(this.pluginId, this.file.value))
        .subscribe(() => {
          this.filePresentAtInit = this.file.get('fileName')?.value !== null;
          this.fileChanged = false;
        }));
    }
  }

  public selectFile(event: Event): void {
    const fileToUpload = (event.target as HTMLInputElement).files?.item(0);

    if (fileToUpload !== null && fileToUpload !== undefined) {
      this.file.get('fileName')?.setValue(fileToUpload.name);
      this.file.get('content')?.setValue(fileToUpload);
      this.fileChanged = true;
    }
  }

  public downloadFile(): void {
    if (this.isInCustomerConfigurationMode) {
      this.customersService.fetchPluginCustomerConfigurationFile(this.pluginId, this.customerId, this.file.get('key')?.value);
    }
    else {
      this.pluginsService.fetchDefaultConfigurationFile(this.pluginId, this.file.get('key')?.value);
    }
  }

  public removeFile(): void {
    this.file.get('fileName')?.setValue(null);
    this.file.get('content')?.setValue(undefined);
    this.fileChanged = this.filePresentAtInit ? true : false;

    // Fix re-select the same file input after remove
    this.fileInput.nativeElement.value = '';
  }

  public canDownload(): boolean {
    return this.filePresentAtInit && !this.fileChanged;
  }

  public canSave(): boolean {
    return this.fileChanged;
  }

  public canRemove(): boolean {
    return this.file.get('fileName')?.value !== null;
  }

  public canMaintain(): boolean {
    return this.isInCustomerConfigurationMode ? this.accessService.canMaintainCustomers() : this.accessService.canMaintainPlugins();
  }
}
